import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { GitPullRequest, ExternalLink, FileClock, CheckCircle2, Loader2, AlertCircle, Trash2 } from "lucide-react";
import { api } from "../../lib/api";

const STATUS_META = {
  opened: { label: "opened", cls: "border-accent text-[hsl(42,55%,40%)]", icon: CheckCircle2 },
  opening: { label: "opening", cls: "border-secondary text-secondary", icon: Loader2 },
  failed: { label: "failed", cls: "border-primary text-primary", icon: AlertCircle },
  draft: { label: "dry-run", cls: "border-muted-foreground text-muted-foreground", icon: FileClock },
};

export default function Deliveries({ onOpenSignal }) {
  const [prs, setPrs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/prs").then((r) => setPrs(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const discard = async (p) => {
    try {
      await api.delete(`/friction/${p.signal_id}/pr`);
      toast.success("Draft discarded");
      load();
    } catch (e) { toast.error("Could not discard"); }
  };

  if (loading) {
    return <div className="font-mono text-sm text-muted-foreground flex items-center gap-2"><span className="w-2 h-2 bg-primary live-dot" /> Loading deliveries…</div>;
  }

  if (prs.length === 0) {
    return (
      <div className="border-2 border-dashed border-border p-10 text-center">
        <GitPullRequest size={28} className="text-primary mx-auto mb-3" />
        <div className="font-mono text-sm text-muted-foreground">No deliveries yet. Open a signal and prepare a Pull Request from the Diagnostic Parser.</div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border divide-y divide-border">
      {prs.map((p) => {
        const meta = STATUS_META[p.status] || STATUS_META.draft;
        const Icon = meta.icon;
        return (
          <div key={p.id} className="px-4 py-4 flex items-center gap-4" data-testid={`delivery-row-${p.issue_number}`}>
            <div className={`w-9 h-9 flex items-center justify-center border-2 border-foreground ${p.status === "opened" ? "bg-accent text-foreground" : "bg-card"}`}>
              <Icon size={16} className={p.status === "opening" ? "animate-spin" : ""} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-mono text-sm font-medium truncate">{p.pr_title}</div>
              <div className="font-mono text-[11px] text-muted-foreground truncate">{p.repo_full_name} · branch {p.branch_name}{p.status === "failed" && p.error ? ` · ${p.error}` : ""}</div>
            </div>
            <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border ${meta.cls}`}>{meta.label}</span>
            {p.status === "opened" && p.pr_url ? (
              <a href={p.pr_url} target="_blank" rel="noreferrer" data-testid={`delivery-link-${p.issue_number}`}
                className="font-mono text-xs flex items-center gap-1 text-secondary hover:text-primary transition-colors">
                PR #{p.pr_number} <ExternalLink size={12} />
              </a>
            ) : (
              <>
                <button onClick={() => onOpenSignal(p.signal_id)} data-testid={`delivery-review-${p.issue_number}`}
                  className="btn btn-outline btn-sm">
                  Review
                </button>
                <button onClick={() => discard(p)} data-testid={`delivery-discard-${p.issue_number}`} title="Discard draft"
                  className="p-1.5 border border-border hover:border-primary hover:text-primary transition-colors">
                  <Trash2 size={14} />
                </button>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
